/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 *
 * "Bu ekranı açıkla" yeteneği için ekranın ANLAMSAL özetini çıkarır.
 * Kurumsal kural: yalnızca etiket/yapı toplanır — kullanıcı VERİSİ (input değerleri,
 * hücre içerikleri) toplanmaz (PII-güvenli). Sonuç AI asistana `pageContext.screenContext`
 * olarak postMessage ile geçer; asistan bunu deterministik "ekran bağlamı" olarak kullanır.
 */

export interface ScreenContext {
  path: string;
  title: string;
  /** Görünür başlıklar (h1-h3) */
  headings: string[];
  /** Aktif sekme etiketi (varsa) */
  activeTab?: string;
  /** Bölüm/kart başlıkları */
  sections: string[];
  /** Form alan etiketleri (DEĞERLER DEĞİL) */
  fields: string[];
  /** Görünür birincil aksiyon/buton etiketleri */
  actions: string[];
  /** Tablo kolon başlıkları */
  tableColumns: string[];
  /** Açık modal/diyalog başlığı (varsa) */
  openDialog?: string;
}

const MAX_ITEMS = 24;
const MAX_TEXT = 80;

function isVisible(el: Element): boolean {
  const node = el as HTMLElement;
  if (!node || typeof node.getBoundingClientRect !== 'function') return false;
  const style = window.getComputedStyle(node);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
  const rect = node.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function clean(text: string | null | undefined): string {
  return String(text ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT);
}

function uniqList(values: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    const v = clean(raw);
    if (!v || seen.has(v.toLowerCase())) continue;
    seen.add(v.toLowerCase());
    out.push(v);
    if (out.length >= MAX_ITEMS) break;
  }
  return out;
}

function queryVisibleText(root: ParentNode, selector: string): string[] {
  const els = Array.from(root.querySelectorAll(selector));
  return els.filter(isVisible).map((el) => el.textContent);
}

/** Bir form kontrolünün görünen etiketini çözer (label/aria/placeholder) — DEĞERİ değil. */
function fieldLabel(el: Element): string {
  const node = el as HTMLElement;
  const id = node.getAttribute('id');
  if (id) {
    const lbl = document.querySelector(`label[for="${CSS.escape(id)}"]`);
    if (lbl?.textContent) return lbl.textContent;
  }
  const aria = node.getAttribute('aria-label');
  if (aria) return aria;
  const wrapLabel = node.closest('label');
  if (wrapLabel?.textContent) return wrapLabel.textContent;
  const ph = node.getAttribute('placeholder');
  if (ph) return ph;
  const name = node.getAttribute('name');
  return name || '';
}

/**
 * Mevcut ekranın anlamsal özetini döndürür. Tarayıcı yoksa (SSR) minimal döner.
 */
export function collectScreenContext(): ScreenContext {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { path: '', title: '', headings: [], sections: [], fields: [], actions: [], tableColumns: [] };
  }

  // Açık modal varsa bağlamı modal içeriğine daralt (kullanıcı muhtemelen onu soruyor).
  const dialog = Array.from(document.querySelectorAll('[role="dialog"], .modal-content'))
    .filter(isVisible)
    .pop() as HTMLElement | undefined;
  const mainContent = document.querySelector('[data-qp-main-content="true"], .main-content') as HTMLElement | null;
  const scope: ParentNode = dialog ?? mainContent ?? document.body;

  const activeTabEl = Array.from(
    document.querySelectorAll('[role="tab"][aria-selected="true"], .tab.active, .nav-tab.active'),
  ).filter(isVisible)[0];

  const headings = uniqList(queryVisibleText(scope, 'h1, h2, h3'));
  const sections = uniqList(
    queryVisibleText(scope, '.card-title, .section-title, .panel-title, [class*="-title"]'),
  );
  const fields = uniqList(
    Array.from(scope.querySelectorAll('input, select, textarea'))
      .filter((el) => isVisible(el) && (el as HTMLInputElement).type !== 'hidden')
      .map(fieldLabel),
  );
  const actions = uniqList(
    Array.from(scope.querySelectorAll('button, [role="button"], a.btn, .btn'))
      .filter(isVisible)
      .map((el) => el.textContent || el.getAttribute('aria-label') || el.getAttribute('title')),
  );
  const tableColumns = uniqList(queryVisibleText(scope, 'thead th, [role="columnheader"]'));

  return {
    path: window.location.pathname,
    title: clean(document.title),
    headings,
    activeTab: activeTabEl ? clean(activeTabEl.textContent) || undefined : undefined,
    sections,
    fields,
    actions,
    tableColumns,
    openDialog: dialog ? clean(headings[0] || dialog.querySelector('h1,h2,h3')?.textContent) || undefined : undefined,
  };
}
